import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shippingAPI } from '../../services/api';
import { FiTruck, FiBox, FiClock, FiDollarSign, FiArrowLeft, FiCheck } from 'react-icons/fi';
import LoadingSpinner from '../../components/LoadingSpinner';

const ShippingRates = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedRate, setSelectedRate] = useState(null);
  const [rates, setRates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get shipment data from navigation state
  const shipmentData = location.state?.shipmentData || {
    weight: 50,
    volume: 5,
    shippingMethod: 'courier',
    items: [],
    sourceAddress: {},
    destinationAddress: {}
  };
  
  useEffect(() => {
    // Simulate loading time for demo
    setIsLoading(true);
    setTimeout(() => {
      const calculatedRates = shippingAPI.calculateDemoRates(
        shipmentData.weight,
        shipmentData.volume,
        shipmentData.shippingMethod
      );
      setRates(calculatedRates);
      setIsLoading(false);
    }, 1500);
  }, [shipmentData]);
  
  const handleRateSelection = (rate) => {
    setSelectedRate(rate);
  };
  
  const handleConfirmRate = () => {
    // Navigate to review page with selected rate
    navigate('/customer/shipping/review', {
      state: {
        shipmentData,
        selectedRate
      }
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Calculating shipping rates...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Steps */}
        <nav aria-label="Progress" className="mb-8">
          <ol className="flex items-center">
            <li className="relative pr-8 sm:pr-20 text-indigo-600">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="h-0.5 w-full bg-indigo-600"></div>
              </div>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-gray-500">
                Select Company & Request
              </div>
            </li>
            
            <li className="relative pr-8 sm:pr-20 text-indigo-600">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="h-0.5 w-full bg-indigo-600"></div>
              </div>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-gray-500">
                Details
              </div>
            </li>
            
            <li className="relative pr-8 sm:pr-20 text-indigo-600">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="h-0.5 w-full bg-gray-200"></div>
              </div>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <span>3</span>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-indigo-600">
                Shipping
              </div>
            </li>
            
            <li className="relative text-gray-500">
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white border-2 border-gray-300">
                <span>4</span>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-gray-500">
                Review
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-4"
          >
            <FiArrowLeft className="mr-2" />
            Back to Shipping Details
          </button>
          
          <h1 className="text-2xl font-semibold text-gray-900">Shipping Rates</h1>
          <p className="mt-2 text-sm text-gray-700">
            Choose the best shipping option for your shipment
          </p>
        </div>
        
        {/* Shipment Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-2">Shipment Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Weight:</span> {shipmentData.weight.toFixed(1)} lbs
            </div>
            <div>
              <span className="font-medium">Volume:</span> {shipmentData.volume.toFixed(1)} cu ft
            </div>
            <div>
              <span className="font-medium">Method:</span> 
              <span className="capitalize ml-1">{shipmentData.shippingMethod}</span>
            </div>
          </div>
        </div>
        
        {/* Shipping Method Header */}
        <div className="flex items-center mb-6">
          {shipmentData.shippingMethod === 'courier' ? (
            <FiBox className="text-blue-500 mr-2 h-6 w-6" />
          ) : (
            <FiTruck className="text-green-500 mr-2 h-6 w-6" />
          )}
          <h2 className="text-xl font-medium text-gray-900">
            {shipmentData.shippingMethod === 'courier' ? 'Courier Services' : 'Freight Services'}
          </h2>
        </div>
        
        {/* Rates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {rates.map((rate, index) => (
            <div
              key={index}
              onClick={() => handleRateSelection(rate)}
              className={`relative p-6 border rounded-lg cursor-pointer transition-all duration-200 ${
                selectedRate?.carrier === rate.carrier && selectedRate?.service === rate.service
                  ? 'border-indigo-500 bg-indigo-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {/* Selection Indicator */}
              {selectedRate?.carrier === rate.carrier && selectedRate?.service === rate.service && (
                <div className="absolute top-4 right-4">
                  <div className="bg-indigo-500 text-white rounded-full p-1">
                    <FiCheck className="h-4 w-4" />
                  </div>
                </div>
              )}
              
              {/* Carrier Logo/Name */}
              <div className="flex items-center mb-3">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-sm font-bold text-gray-600">
                    {rate.carrier.split(' ')[0].substring(0, 3).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{rate.carrier}</div>
                  <div className="text-sm text-gray-500">{rate.service}</div>
                </div>
              </div>
              
              {/* Price */}
              <div className="mb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(rate.cost)}
                </div>
                {shipmentData.shippingMethod === 'freight' && (
                  <div className="text-xs text-gray-500">
                    Base: $50 + {shipmentData.volume.toFixed(1)} cu ft @ $0.75/cu ft
                  </div>
                )}
              </div>
              
              {/* Transit Time */}
              <div className="flex items-center text-sm text-gray-600">
                <FiClock className="mr-2 h-4 w-4" />
                {rate.transit_days} business days
              </div>
              
              {/* Features for freight */}
              {shipmentData.shippingMethod === 'freight' && (
                <div className="mt-3 text-xs text-gray-500">
                  <div>• Terminal-to-terminal</div>
                  <div>• Lift gate available</div>
                  <div>• Inside delivery available</div>
                </div>
              )}
              
              {/* Best value indicator */}
              {index === 0 && (
                <div className="absolute -top-2 left-4">
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    Best Value
                  </span>
                </div>
              )}
              
              {/* Fastest indicator */}
              {shipmentData.shippingMethod === 'courier' && rate.transit_days === '1' && (
                <div className="absolute -top-2 right-4">
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    Fastest
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Special Services for Freight */}
        {shipmentData.shippingMethod === 'freight' && selectedRate && (
          <div className="bg-white border rounded-lg p-6 mb-6">
            <h3 className="font-medium text-gray-900 mb-4">Additional Services</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-sm">Liftgate Service (+$75)</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-sm">Inside Delivery (+$125)</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-sm">White Glove (+$200)</span>
              </label>
            </div>
          </div>
        )}
        
        {/* Rate Comparison Table */}
        <div className="bg-white border rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="font-medium text-gray-900">Rate Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Carrier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transit Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Select
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rates.map((rate, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {rate.carrier}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rate.service}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(rate.cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rate.transit_days} days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="radio"
                        name="selectedRate"
                        checked={selectedRate?.carrier === rate.carrier && selectedRate?.service === rate.service}
                        onChange={() => handleRateSelection(rate)}
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
          
          <button
            onClick={handleConfirmRate}
            disabled={!selectedRate}
            className={`px-6 py-2 rounded-md ${
              selectedRate
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue with {selectedRate ? `${selectedRate.carrier} ${selectedRate.service}` : 'Selected Rate'}
          </button>
        </div>
        
        {/* Demo Notice */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <FiDollarSign className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Demo Rates</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  These are demonstration rates for preview purposes. Actual rates will be calculated 
                  using live carrier APIs and may vary based on additional factors such as dimensional 
                  weight, fuel surcharges, and seasonal adjustments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingRates; 